/**
 * Day header counts are "A | B | C":
 *  A = total appointments that day
 *  B = appointments where the patient was NOT a member when the service started.
 *      Never shrinks once the patient converts — it is the number of conversion
 *      opportunities.
 *  C = how many of B converted to a membership after that service start (same
 *      day or later). A conversion is credited to the patient's first-visit day,
 *      not the purchase day.
 * A patient who already had a membership when the service started is in neither
 * B nor C. The backend compares against service_start_time; the client-side
 * fallback below only has the appointment time.
 */
export type ScheduleAppointmentForCounts = {
  appointmentTime?: string
  count?: string | number
  isMember: boolean
  wasNonMember?: boolean
  was_non_member?: boolean
  convertedToMember?: boolean
  converted_to_member?: boolean
  memberConvertedAt?: string
  member_converted_at?: string
  nonMemberCount?: string | number
  convertedCount?: string | number
  converted_count?: string | number
}

export type DayScheduleCounts = {
  totalCount: number
  nonMemberCount: number
  convertedCount: number
}

export const emptyDayScheduleCounts = (): DayScheduleCounts => ({
  totalCount: 0,
  nonMemberCount: 0,
  convertedCount: 0,
})

export const getAppointmentCount = (item: ScheduleAppointmentForCounts): number => Number(item.count) || 1

/** Patient became a member after this appointment started (same day counts). */
export const isConvertedToMember = (item: ScheduleAppointmentForCounts, appointmentDate: Date): boolean => {
  if (item.convertedToMember ?? item.converted_to_member) {
    return true
  }

  const convertedAt = item.memberConvertedAt ?? item.member_converted_at
  if (convertedAt) {
    const convertedTime = new Date(convertedAt).getTime()
    return Number.isFinite(convertedTime) && convertedTime > appointmentDate.getTime()
  }

  return false
}

/** Counts slots booked as non-member; does not drop when patient later becomes a member. */
export const wasBookedAsNonMember = (item: ScheduleAppointmentForCounts, appointmentDate: Date): boolean => {
  if (typeof item.wasNonMember === 'boolean') return item.wasNonMember
  if (typeof item.was_non_member === 'boolean') return item.was_non_member
  if (isConvertedToMember(item, appointmentDate)) return true
  return !item.isMember
}

const getApiNumericCount = (value: string | number | undefined): number | null => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export const getNonMemberCountContribution = (item: ScheduleAppointmentForCounts, appointmentDate: Date): number => {
  const apiCount = getApiNumericCount(item.nonMemberCount)
  if (apiCount != null) return apiCount

  const count = getAppointmentCount(item)
  return wasBookedAsNonMember(item, appointmentDate) ? count : 0
}

export const getConvertedCountContribution = (item: ScheduleAppointmentForCounts, appointmentDate: Date): number => {
  const apiCount = getApiNumericCount(item.convertedCount ?? item.converted_count)
  if (apiCount != null) return apiCount

  const count = getAppointmentCount(item)
  return isConvertedToMember(item, appointmentDate) ? count : 0
}

export const accumulateDayScheduleCounts = (
  counts: Record<string, DayScheduleCounts>,
  dayKey: string,
  item: ScheduleAppointmentForCounts,
  appointmentDate: Date,
) => {
  if (!counts[dayKey]) {
    counts[dayKey] = emptyDayScheduleCounts()
  }

  const count = getAppointmentCount(item)
  counts[dayKey].totalCount += count
  counts[dayKey].nonMemberCount += getNonMemberCountContribution(item, appointmentDate)
  counts[dayKey].convertedCount += getConvertedCountContribution(item, appointmentDate)
}
